import { useUpdateEntity } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import EntityInterface from 'common/types/entities/EntityInterface'
import React from 'react'
import { setObjectProperty } from 'src/editor/shared/utils/set-object-property'
import { Repeat04Icon } from '../icons/repeat-04-icon'
import { ButtonSizeEnum, GhostIconButton } from '../ui-lib/buttons'

export type ResetEntityStylesProps<
  Entity extends Partial<EntityInterface>,
  PropertyKey1 extends keyof Entity,
  PropertyKey2 extends keyof Entity[PropertyKey1],
  PropertyKey3 extends keyof Entity[PropertyKey1][PropertyKey2],
  MobilePropertyKey1 extends keyof Entity,
  MobilePropertyKey2 extends keyof Entity[MobilePropertyKey1],
  MobilePropertyKey3 extends
    keyof Entity[MobilePropertyKey1][MobilePropertyKey2],
> = {
  className?: string
  entity: Entity
  defaultEntity: Entity | undefined
  keys: Array<
    | PropertyKey1
    | [PropertyKey1, PropertyKey2]
    | [PropertyKey1, PropertyKey2, PropertyKey3]
  >
  mobileKeys?: Array<
    | MobilePropertyKey1
    | [MobilePropertyKey1, MobilePropertyKey2]
    | [MobilePropertyKey1, MobilePropertyKey2, MobilePropertyKey3]
  >
}

export const ResetEntityStyles = <
  Entity extends Partial<EntityInterface>,
  PropertyKey1 extends keyof Entity,
  PropertyKey2 extends keyof Entity[PropertyKey1],
  PropertyKey3 extends keyof Entity[PropertyKey1][PropertyKey2],
  MobilePropertyKey1 extends keyof Entity,
  MobilePropertyKey2 extends keyof Entity[MobilePropertyKey1],
  MobilePropertyKey3 extends
    keyof Entity[MobilePropertyKey1][MobilePropertyKey2],
>({
  className,
  entity,
  defaultEntity,
  keys,
  mobileKeys,
}: ResetEntityStylesProps<
  Entity,
  PropertyKey1,
  PropertyKey2,
  PropertyKey3,
  MobilePropertyKey1,
  MobilePropertyKey2,
  MobilePropertyKey3
>) => {
  const { isMobile } = useDeviceModeContext()

  const currentKeys = isMobile ? mobileKeys || keys : keys

  const updateEntity = useUpdateEntity<Entity>()
  const onResetStyles = () => {
    if (defaultEntity) {
      updateEntity(
        currentKeys.reduce(
          (acc, key) =>
            setObjectProperty(
              acc,
              defaultEntity,
              key as
                | PropertyKey1
                | [PropertyKey1, PropertyKey2]
                | [PropertyKey1, PropertyKey2, PropertyKey3],
            ),
          entity as Entity,
        ),
      )
    }
  }

  return (
    defaultEntity && (
      <GhostIconButton
        className={className}
        size={ButtonSizeEnum.Small}
        onClick={onResetStyles}
        icon={<Repeat04Icon />}
      />
    )
  )
}
